import { useMemo } from "react";
import {
	validateFreeArea,
	type ClientToHostMessage,
	type FreeArea,
	type MessagePayload,
	type ValidationError,
} from "@rubyplay/effw-connector";
import { useRegisterCommands, type Command } from "@/commands/CommandRegistryContext";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import { useSnapshotBridge } from "@/contexts/SnapshotBridgeContext";
import { CHEAT_COMMANDS } from "@/cheats/cheatCommandNames";
import { getHostSessionStore } from "@/host/HostSessionStore";
import {
	buildFatalPayload,
	getEfRuntimeInstanceId,
	hasFatalBeenReported,
	markFatalReported,
} from "@/runtime/efRuntimeProcess";
import { debugLog, debugWarn } from "@/utils";
import { parseBetPayload } from "@/shared/parsers/betPayload";

type FatalCategory = MessagePayload<ClientToHostMessage, "FatalError">["category"];

type DeepPartial<T> = {
	[K in keyof T]?: NonNullable<T[K]> extends object
		? DeepPartial<NonNullable<T[K]>>
		: T[K];
};

export type WidgetPositionPatch = Omit<
	DeepPartial<FreeArea>,
	"id" | "platform" | "orientation"
> & {
	x?: number;
	y?: number;
};

type WindowWithCaptainUpTestFlags = Window & {
	__EFW_TEST_CAPTAINUP_AUTH_TIMEOUT__?: boolean;
	__EFW_TEST_CAPTAINUP_AUTH_BAD_REQUEST__?: boolean;
	__EFW_TEST_CAPTAINUP_AUTH_NOT_FOUND__?: boolean;
	__EFW_TEST_CAPTAINUP_AUTH_SERVER_ERROR__?: boolean;
	__EFW_TEST_CAPTAINUP_SDK_LOAD_FAILURE__?: boolean;
	__EFW_TEST_CAPTAINUP_SDK_INIT_FAILURE__?: boolean;
};

const EFW_CHEAT_BET_UPDATED_EVENT = "efw:cheat:bet-updated";

function parseFatalCategory(value: unknown): FatalCategory {
	if (value === "RUNTIME" || value === "UNHANDLED_REJECTION") {
		return value;
	}

	return "RUNTIME";
}

function parseFatalMessage(value: unknown): string {
	if (typeof value === "string" && value.trim().length > 0) {
		return value.trim();
	}

	return "[Cheats] forced fatal error";
}

function buildCheatFatalPayload(payload?: unknown): MessagePayload<ClientToHostMessage, "FatalError"> {
	if (payload && typeof payload === "object") {
		const candidate = payload as {
			category?: unknown;
			message?: unknown;
			stack?: unknown;
		};

		const base = buildFatalPayload(
			parseFatalCategory(candidate.category),
			new Error(parseFatalMessage(candidate.message))
		);

		return {
			...base,
			stack:
				typeof candidate.stack === "string" && candidate.stack.trim().length > 0
					? candidate.stack
					: base.stack,
		};
	}

	return buildFatalPayload("RUNTIME", new Error("[Cheats] forced fatal error"));
}

function formatValidationErrors(errors: readonly ValidationError[]): string {
	return errors
		.map((error) => `- ${error.path.join(".") || "payload"}: ${error.message}`)
		.join("\n");
}

export function validateWidgetPositionPatch(
	area: Readonly<FreeArea>,
	patch: WidgetPositionPatch
) {
	return validateFreeArea({
		...area,
		...patch,
	});
}

export function mergeWidgetPosition(
	area: Readonly<FreeArea>,
	patch: WidgetPositionPatch
): unknown {
	if (Object.keys(patch).length === 0) {
		throw new Error("expected at least one FreeArea property to update");
	}

	if ("id" in patch || "platform" in patch || "orientation" in patch) {
		throw new Error("id, platform, and orientation cannot be changed by this cheat");
	}

	if (patch.x !== undefined && !Number.isFinite(patch.x)) {
		throw new Error("x must be a finite number");
	}

	if (patch.y !== undefined && !Number.isFinite(patch.y)) {
		throw new Error("y must be a finite number");
	}

	const { x, y, ...freeAreaPatch } = patch;
	const nextAbsolute =
		patch.absolute !== undefined || x !== undefined || y !== undefined
			? {
					...area.absolute,
					...patch.absolute,
					...(x !== undefined ? { x } : {}),
					...(y !== undefined ? { y } : {}),
				}
			: area.absolute;
	const nextRelative = patch.relative !== undefined
		? {
				...area.relative,
				...patch.relative,
				...(patch.relative.offset !== undefined
					? {
							offset: {
								...area.relative?.offset,
								...patch.relative.offset,
							},
						}
					: {}),
			}
		: area.relative;
	const nextHomeButton = patch.withHomeButton !== undefined
		? {
				...area.withHomeButton,
				...patch.withHomeButton,
				...(patch.withHomeButton.absolute !== undefined
					? {
							absolute: {
								...area.withHomeButton?.absolute,
								...patch.withHomeButton.absolute,
							},
						}
					: {}),
				...(patch.withHomeButton.relative !== undefined
					? {
							relative: {
								...area.withHomeButton?.relative,
								...patch.withHomeButton.relative,
								...(patch.withHomeButton.relative.offset !== undefined
									? {
											offset: {
												...area.withHomeButton?.relative?.offset,
												...patch.withHomeButton.relative.offset,
											},
										}
									: {}),
							},
						}
					: {}),
			}
		: area.withHomeButton;

	return {
		...area,
		...freeAreaPatch,
		absolute: nextAbsolute,
		relative: nextRelative,
		withHomeButton: nextHomeButton,
	};
}

export function EFProtocolCheatCommands() {
	const {
		sendHeartBeat,
		sendFatalError,
		sendClientReady,
		sendUpdateFreeArea,
	} = useCommunicator();
	const { triggerAllHitAreaWidgetSnapshots } = useSnapshotBridge();

	const commands = useMemo<Command[]>(() => {
		return [
			{
				name: CHEAT_COMMANDS.ef.setWidgetPosition,
				execute: async (payload?: unknown) => {
					try {
						const hostSessionStore = getHostSessionStore();
						const snapshot = hostSessionStore.getSnapshot();

						if (!snapshot.configured || !snapshot.config) {
							throw new Error("the host session is not configured yet");
						}

						const activeIndex = snapshot.configuredFreeAreas.findIndex(
							(area) =>
								area.platform === snapshot.config?.platform &&
								area.orientation === snapshot.config?.orientation
						);

						if (activeIndex < 0) {
							throw new Error(
								"no FreeArea matches the current platform and orientation"
							);
						}

						const patch = payload as WidgetPositionPatch;
						const patchValidation = validateWidgetPositionPatch(
							snapshot.configuredFreeAreas[activeIndex],
							patch
						);

						if (!patchValidation.success) {
							console.error(
								`[Cheats] setWidgetPosition rejected:\n${formatValidationErrors(patchValidation.errors)}`,
								{ payload, errors: patchValidation.errors }
							);
							return;
						}

						const merged = mergeWidgetPosition(
							snapshot.configuredFreeAreas[activeIndex],
							patch
						);
						const validation = validateFreeArea(merged);

						if (!validation.success) {
							console.error(
								`[Cheats] setWidgetPosition rejected:\n${formatValidationErrors(validation.errors)}`,
								{ payload, merged, errors: validation.errors }
							);
							return;
						}

						const nextConfiguredFreeAreas =
							snapshot.configuredFreeAreas.map((area, index) =>
								index === activeIndex ? validation.data : { ...area }
							);

						sendUpdateFreeArea({ freeArea: validation.data });

						hostSessionStore.dispatch({
							type: "CONFIGURED_FREE_AREAS_UPDATED",
							payload: { freeAreas: nextConfiguredFreeAreas },
						});

						console.log("[Cheats] setWidgetPosition", {
							areaId: validation.data.id,
							patch,
							freeArea: validation.data,
						});
					} catch (error) {
						console.error("[Cheats] setWidgetPosition failed", error);
					}
				},
			},
			{
				name: CHEAT_COMMANDS.ef.sendHeartBeat,
				execute: async () => {
					const payload: MessagePayload<ClientToHostMessage, "HeartBeat"> = {
						instanceId: getEfRuntimeInstanceId(),
						timestamp: Date.now(),
						state: "healthy",
					};

					debugLog("[Cheats] sendHeartBeat", payload);

					sendHeartBeat(payload);
				},
			},
			{
				name: CHEAT_COMMANDS.ef.sendClientReady,
				execute: async () => {
					const payload: MessagePayload<ClientToHostMessage, "ClientReady"> = {
						instanceId: getEfRuntimeInstanceId(),
					};

					debugLog("[Cheats] sendClientReady", payload);

					sendClientReady(payload);
				},
			},
			{
				name: CHEAT_COMMANDS.ef.sendFatalError,
				execute: async (payload?: unknown) => {
					if (hasFatalBeenReported()) {
						debugWarn("[Cheats] sendFatalError ignored because fatal was already reported");
						return;
					}

					const fatalPayload = buildCheatFatalPayload(payload);

					markFatalReported();

					debugLog("[Cheats] sendFatalError", fatalPayload);

					sendFatalError(fatalPayload);
				},
			},
			{
				name: CHEAT_COMMANDS.ef.sendBetUpdated,
				execute: async (payload?: unknown) => {
					const parsed = parseBetPayload(payload);

					if (!parsed) {
						debugWarn("[Cheats] sendBetUpdated ignored because payload is invalid", {
							payload,
						});
						return;
					}

					debugLog("[Cheats] sendBetUpdated", parsed);

					window.dispatchEvent(
						new CustomEvent(EFW_CHEAT_BET_UPDATED_EVENT, {
							detail: parsed,
						})
					);
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpAuthTimeout,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_AUTH_TIMEOUT__ = true;

					debugLog("[Cheats] triggerCaptainUpAuthTimeout armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpAuthBadRequest,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_AUTH_BAD_REQUEST__ = true;

					debugLog("[Cheats] triggerCaptainUpAuthBadRequest armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpAuthNotFound,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_AUTH_NOT_FOUND__ = true;

					debugLog("[Cheats] triggerCaptainUpAuthNotFound armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpAuthServerError,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_AUTH_SERVER_ERROR__ = true;

					debugLog("[Cheats] triggerCaptainUpAuthServerError armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpSdkLoadFailure,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_SDK_LOAD_FAILURE__ = true;

					debugLog("[Cheats] triggerCaptainUpSdkLoadFailure armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpSdkInitFailure,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_SDK_INIT_FAILURE__ = true;

					debugLog("[Cheats] triggerCaptainUpSdkInitFailure armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerAllHitAreaWidgetSnapshots,
				execute: async () => {
					debugLog("[Cheats] triggerAllHitAreaWidgetSnapshots");
					triggerAllHitAreaWidgetSnapshots();
				},
			},
		];
	}, [
		sendClientReady,
		sendFatalError,
		sendHeartBeat,
		sendUpdateFreeArea,
		triggerAllHitAreaWidgetSnapshots,
	]);

	useRegisterCommands(commands);

	return null;
}

export default EFProtocolCheatCommands;
